import {
  DateTimePicker,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shigo-ui/ui';
import { DashboardPageFormBody } from '@shigo-ui/dashboard-ui';

import type { MadBuyLineItemGeneralForm } from '../../lineItemsModel';
import { dashboardT } from '../../../../../lib/messages';
import { FormField } from './LineItemEditorFields';
import type { LineItemFormUpdate } from './LineItemEditorTypes';
import { LineItemPacingSelect } from './LineItemPacingSelect';
import { LineItemScheduleGrid } from './LineItemScheduleGrid';

export function LineItemScheduleFields({
  errors,
  form,
  submitted,
  timezoneLabel,
  updateField,
}: {
  errors: Partial<Record<keyof MadBuyLineItemGeneralForm, string>>;
  form: MadBuyLineItemGeneralForm;
  submitted: boolean;
  timezoneLabel: string;
  updateField: LineItemFormUpdate;
}) {
  return (
    <DashboardPageFormBody
      width="lg"
      title={dashboardT('madBuy.lineItems.schedule')}
      description={dashboardT('madBuy.lineItems.scheduleDescription')}
    >
      <div className="mx-auto grid w-full max-w-3xl gap-4 md:grid-cols-2">
        <FormField
          label={`(${timezoneLabel}) Start date`}
          required
          error={submitted ? errors.startDate : undefined}
        >
          <DateTimePicker
            label={dashboardT('madBuy.lineItems.startDate')}
            value={form.startDate}
            onChange={(value: string) => {
              updateField('startDate', value);
            }}
          />
        </FormField>
        <FormField
          label={`(${timezoneLabel}) End date`}
          required
          error={submitted ? errors.endDate : undefined}
        >
          <DateTimePicker
            label={dashboardT('madBuy.lineItems.endDate')}
            minDateTime={form.startDate || undefined}
            value={form.endDate}
            onChange={(value: string) => {
              updateField('endDate', value);
            }}
          />
        </FormField>
      </div>

      <div className="mx-auto grid w-full max-w-3xl gap-4 md:grid-cols-3">
        <FormField label={dashboardT('madBuy.lineItems.scheduling')}>
          <Select
            value={String(form.scheduling)}
            onValueChange={(value) => {
              const nextScheduling = value === 'true';
              updateField('scheduling', nextScheduling);
              if (!nextScheduling) {
                updateField('dayTimeActivities', []);
              }
            }}
          >
            <SelectTrigger aria-label={dashboardT('madBuy.lineItems.scheduling')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="false">{dashboardT('madBuy.lineItems.allTheTime')}</SelectItem>
              <SelectItem value="true">{dashboardT('madBuy.lineItems.onSchedule')}</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
        <FormField label={dashboardT('madBuy.lineItems.pacing')}>
          <LineItemPacingSelect
            ariaLabel={dashboardT('madBuy.lineItems.pacing')}
            value={form.pacing}
            onValueChange={(value) => {
              updateField('pacing', value);
            }}
          />
        </FormField>
        <FormField label={dashboardT('madBuy.lineItems.pacingPerHour')}>
          <LineItemPacingSelect
            ariaLabel={dashboardT('madBuy.lineItems.pacingPerHour')}
            value={form.pacingPerHour}
            onValueChange={(value) => {
              updateField('pacingPerHour', value);
            }}
          />
        </FormField>
      </div>

      {form.scheduling ? (
        <LineItemScheduleGrid
          activities={form.dayTimeActivities}
          timezoneLabel={timezoneLabel}
          onActivitiesChange={(activities) => {
            updateField('dayTimeActivities', activities);
          }}
        />
      ) : (
        <div className="border-border bg-muted/20 mx-auto w-full max-w-3xl rounded-md border px-4 py-3">
          <p className="text-muted-foreground text-sm">
            {dashboardT('madBuy.lineItems.schedulingAllHours')}
          </p>
        </div>
      )}
      {submitted && errors.dayTimeActivities ? (
        <p role="alert" className="text-destructive mx-auto w-full max-w-3xl text-sm">
          {errors.dayTimeActivities}
        </p>
      ) : null}
    </DashboardPageFormBody>
  );
}
